// src/hooks/useSEO.ts - Enhanced SEO hook with canonical URL management
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { seoService } from '../services/seoService';
import CanonicalUrlManager from '../utils/canonicalUrl';

interface CustomSEO {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'video' | 'audio';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
  noIndex?: boolean;
  data?: any; // Additional data for dynamic SEO generation
}

export const useSEO = (customSEO?: CustomSEO) => {
  const location = useLocation();

  useEffect(() => {
    // Check for canonical redirect needs first (only in production)
    if (import.meta.env.VITE_NODE_ENV === 'production' && CanonicalUrlManager.performCanonicalRedirect()) {
      return; // Redirect performed, don't set SEO
    }

    // Get page-specific SEO data
    const seoData = seoService.getPageSEO(location.pathname, customSEO?.data);
    
    // Override with custom SEO data if provided
    if (customSEO) {
      Object.assign(seoData, customSEO);
    }

    // Generate canonical URL
    const canonicalUrl = CanonicalUrlManager.getCurrentCanonical();
    seoData.url = canonicalUrl;

    // Set document title
    const fullTitle = seoData.title || 'StrymHub';
    document.title = fullTitle;
    
    // Function to update or create meta tag
    const setMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set canonical URL using our canonical manager
    CanonicalUrlManager.setCanonical(canonicalUrl);

    // Basic meta tags
    if (seoData.description) {
      setMetaTag('description', seoData.description);
    }
    
    if (seoData.keywords) {
      setMetaTag('keywords', seoData.keywords);
    }
    
    if (seoData.author) {
      setMetaTag('author', seoData.author);
    }
    
    // Robots
    setMetaTag('robots', seoData.noIndex ? 'noindex,nofollow' : 'index,follow');
    
    // Open Graph - use canonical URLs
    setMetaTag('og:type', seoData.type || 'website', true);
    if (seoData.title) {
      setMetaTag('og:title', seoData.title, true);
    }
    if (seoData.description) {
      setMetaTag('og:description', seoData.description, true);
    }
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:site_name', 'StrymHub', true);
    setMetaTag('og:locale', 'en_US', true);
    
    if (seoData.image) {
      setMetaTag('og:image', seoData.image, true);
      setMetaTag('og:image:alt', seoData.title || 'StrymHub Content', true);
    }
    
    // Article specific meta tags
    if (seoData.type === 'article') {
      if (seoData.author) setMetaTag('article:author', seoData.author, true);
      if (seoData.publishedTime) setMetaTag('article:published_time', seoData.publishedTime, true);
      if (seoData.modifiedTime) setMetaTag('article:modified_time', seoData.modifiedTime, true);
      if (seoData.section) setMetaTag('article:section', seoData.section, true);
      if (seoData.tags) {
        seoData.tags.forEach(tag => {
          const tagMeta = document.createElement('meta');
          tagMeta.setAttribute('property', 'article:tag');
          tagMeta.setAttribute('content', tag);
          document.head.appendChild(tagMeta);
        });
      }
    }
    
    // Twitter Card
    setMetaTag('twitter:card', seoData.type === 'video' ? 'player' : 'summary_large_image');
    setMetaTag('twitter:site', '@strymhub');
    
    if (seoData.title) {
      setMetaTag('twitter:title', seoData.title);
    }
    if (seoData.description) {
      setMetaTag('twitter:description', seoData.description);
    }
    
    if (seoData.image) {
      setMetaTag('twitter:image', seoData.image);
    }
    
    // Generate and update structured data if we have content data
    if (customSEO?.data) {
      const contentType = location.pathname.split('/')[1]; // originals, cinema, podcasts, etc.
      const structuredData = seoService.generateStructuredData(contentType.slice(0, -1), customSEO.data);
      
      if (structuredData) {
        // Remove existing structured data
        const existingStructuredData = document.querySelector('script[type="application/ld+json"]');
        if (existingStructuredData) {
          existingStructuredData.remove();
        }

        // Add new structured data
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(structuredData);
        document.head.appendChild(script);
      }
    }

  }, [location.pathname, customSEO]);

  return {
    generateSlug: seoService.generateSlug,
    optimizeDescription: seoService.optimizeDescription,
    generateCanonical: CanonicalUrlManager.generateCanonical,
    getCurrentCanonical: CanonicalUrlManager.getCurrentCanonical,
  };
};

// Default export for backwards compatibility
export default useSEO;
